import { token } from '@cc-shopping/styled-system/tokens';
import { ApprovalOddsErrorCategory } from '@cc-shopping/utils/errors/approvalOddsErrors';
import { datadogRum } from '@datadog/browser-rum';
import {
  AO_RANGES,
  CREDIT_RANGES,
  EXCELLENT_RANGE,
  FAIR_RANGE,
  GOOD_RANGE,
  Likelihood,
} from './constants';

export const getCardsApprovalOddsValues = (approvalOdds: string) => {
  switch (approvalOdds) {
    case 'POOR_AO':
      return {
        label: Likelihood.Poor,
        labelDelta: 0,
        desktopDelta: -38,
        color: token('colors.reds.light'),
        placement: 1,
      };
    case 'FAIR_AO':
      return {
        label: Likelihood.Fair,
        labelDelta: -1,
        desktopDelta: -1,
        color: token('colors.yellows.main'),
        placement: 3,
      };
    case 'GOOD_AO':
      return {
        label: Likelihood.Good,
        labelDelta: 1,
        desktopDelta: 10,
        color: token('colors.nwGreens.light'),
        placement: 4,
      };
    case 'EXCELLENT_AO':
      return {
        label: Likelihood.Excellent,
        labelDelta: 21,
        desktopDelta: 10,
        color: token('colors.greens.main'),
        placement: 5,
      };
    default:
      datadogRum.addError(new Error('Invalid approval odds value'), {
        approvalOdds,
        errorCategory: ApprovalOddsErrorCategory.INVALID_VALUE,
      });
      return null;
  }
};

export const getCreditScoreLabel = (creditScore: number) => {
  if (creditScore < FAIR_RANGE.min) return 'Poor';
  else if (creditScore < GOOD_RANGE.min) return 'Fair';
  else if (creditScore < EXCELLENT_RANGE.min) return 'Good';
  else return 'Excellent';
};

export const getOddsLabel = (min: number, max: number) => {
  const minLabel = getCreditScoreLabel(min);
  const maxLabel = getCreditScoreLabel(max);

  if (minLabel !== maxLabel) return `${minLabel} - ${maxLabel}`;
  return minLabel;
};

export const getSliderPercentage = (creditScore: number) => {
  return CREDIT_RANGES.reduce((sliderPercentage, range) => {
    if (creditScore < range.min) {
      return sliderPercentage;
    }

    const totalRangeLength = range.max - range.min;
    const rangeLength = Math.min(creditScore, range.max) - range.min;
    const rangePercentage = rangeLength / totalRangeLength;
    return sliderPercentage + (1 / CREDIT_RANGES.length) * rangePercentage * 100;
  }, 0);
};

export const getLabelStartColumn = (min: number) => {
  if (min >= EXCELLENT_RANGE.min) return 4;
  if (min >= GOOD_RANGE.min) return 3;
  if (min >= FAIR_RANGE.min) return 2;
  return 1;
};

export const getLabelEndColumn = (max: number) => {
  if (max >= EXCELLENT_RANGE.min) return 5;
  if (max >= GOOD_RANGE.min) return 4;
  if (max >= FAIR_RANGE.min) return 3;
  return 2;
};

export const getApprovalLikelihoodLabel = (approvalLikelihood: number) => {
  let label;

  if (approvalLikelihood < AO_RANGES.LOW.max) {
    label = 'poor';
  } else if (
    approvalLikelihood >= AO_RANGES.MEDIUM.min &&
    approvalLikelihood < AO_RANGES.MEDIUM.max
  ) {
    label = 'good';
  } else {
    label = 'excellent';
  }

  return label;
};
